import kebabCase from 'kebab-case';
import { isEqual } from 'radash';
import React, { memo, useEffect, useRef } from 'react';

import { getCompiler, getInjector } from '@/services/angularjs-bridge';

interface DirectiveWrapperProps {
    name: string;
    props: DirectiveProps;
    className?: string;
}

interface DirectiveProps {
    [key: string]: {
        value: string;
        type: '@' | '=';
    };
}

// eslint-disable-next-line
type AngularScope = any;

/**
 * A directive wrapper where we can inject various angularjs directives from the legacy world
 * into our React application.
 *
 * ATTENTION: - React will re-render the directive in case of `props` change.
 *            - Remember to memoize the passed props using `useMemo` in parent component.
 *
 * This would be removed once the migration is done.
 */
const ReactBridgeDirectiveWrapper = memo(({ name, props, className }: DirectiveWrapperProps) => {
    const directiveRef = useRef<HTMLDivElement>(null);
    const stablePropsRef = useRef<DirectiveProps>(props);
    if (!isEqual(stablePropsRef.current, props)) {
        stablePropsRef.current = props;
    }
    const stableProps = stablePropsRef.current;

    const prepareDirectiveProperties = (props: DirectiveProps) => {

        return Object.keys(props)
            .map(key => {
                const prop = props[key];

                if (prop.type === '=') {
                    // In case of two-way binding, we just attach the property name.
                    return `data-${kebabCase(key)}="${key}"`;
                } else if (prop.type === '@') {
                    // But in case of two-literal binding, we attach the value.
                    return `data-${kebabCase(key)}="${prop.value}"`;
                }
            }).join(' ');
    };

    const bindPropertiesToScope = (scope: AngularScope, props: DirectiveProps) => {

        Object.keys(props)
            .map(key => {
                const prop = props[key];

                // We only bind two-way binding properties.
                if (prop.type === '=') {
                    scope[key] = prop.value;
                }
            });
    };

    useEffect(() => {

        const $injector = getInjector();
        const $compile = getCompiler();
        const scope = $injector.get('$rootScope').$new(true) as AngularScope;

        const directiveProps = prepareDirectiveProperties(stableProps);
        bindPropertiesToScope(scope, stableProps);

        const template = `<${name} ${directiveProps}></${name}>`;
        const compiledTemplate = $compile(template)(scope);

        // Clear the previous content of the directiveRef.current
        while (directiveRef.current?.firstChild) {
            directiveRef.current.removeChild(directiveRef.current.firstChild);
        }

        // Append the compiled template to the directiveRef.current (React component's ref)
        directiveRef.current?.appendChild(compiledTemplate[0]);

        // When un-mounting, let's clean up any previous scopes
        return () => {
            scope.$destroy();
        };

    }, [name, stableProps]);

    return React.createElement('div', { ref: directiveRef, className });

});

ReactBridgeDirectiveWrapper.displayName = 'ReactBridgeDirectiveWrapper';

export { type DirectiveProps, ReactBridgeDirectiveWrapper };
